import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { guardarDestino } from '@/lib/destino';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import { mostrarToast } from '@/lib/formularioGuiado';
import Cabecalho from '@/components/ui/Cabecalho';
import Botao from '@/components/ui/Botao';
import Cartao from '@/components/ui/Cartao';
import Carregando from '@/components/ui/Carregando';
import EstadoVazio from '@/components/ui/EstadoVazio';
import type { AbaPlataforma, EventoPlataforma, PainelPlataforma, SituacaoTela } from './types';
import { horaMinuto, normalizarPainel } from './utils';
import Tiles from './components/Tiles';
import CartaoEvento from './components/CartaoEvento';
import CartaoExclusoes from './components/CartaoExclusoes';
import ModalRegistrarRepasse from './components/ModalRegistrarRepasse';
import AvisosGerais from './components/AvisosGerais';
import Leads from './components/Leads';
import Oracoes from './components/Oracoes';
import AjudarSenha from './components/AjudarSenha';

export default function PlataformaPage() {
  const navigate = useNavigate();
  const { user, pronto } = useAuth();

  const [situacao, setSituacao] = useState<SituacaoTela>('carregando-login');
  const [painel, setPainel] = useState<PainelPlataforma | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [recarregando, setRecarregando] = useState(false);
  const [registrandoAlvo, setRegistrandoAlvo] = useState<EventoPlataforma | null>(null);
  const [testesAberto, setTestesAberto] = useState(false);
  const [aba, setAba] = useState<AbaPlataforma>('resumo');

  const carregar = useCallback(
    async (inicial: boolean) => {
      if (!pronto) return;
      if (!user) {
        guardarDestino('/plataforma');
        navigate(`/entrar?voltar=${encodeURIComponent('/plataforma')}`, { replace: true });
        return;
      }
      if (inicial) setSituacao('carregando-login');
      else setRecarregando(true);
      setErro(null);
      try {
        const { data, error } = await supabase.rpc('plataforma_painel');
        if (error) {
          const codigo = String((error as { code?: string }).code ?? '');
          const msg = String((error as { message?: string }).message ?? '');
          if (codigo === '42501' || msg.includes('Acesso restrito')) {
            setSituacao('restrito');
            return;
          }
          setErro(mensagemAmigavel(error));
          setSituacao('erro');
          return;
        }

        const p = normalizarPainel(data);
        if (!p) {
          setErro('Não foi possível carregar o painel agora.');
          setSituacao('erro');
          return;
        }
        setPainel(p);
        setSituacao('ok');
      } catch (e) {
        setErro(mensagemAmigavel(e));
        setSituacao('erro');
      } finally {
        setRecarregando(false);
      }
    },
    [navigate, pronto, user],
  );

  useEffect(() => {
    void carregar(true);
  }, [carregar]);

  const subtitulo = painel
    ? `Só você e o André veem esta tela · atualizado às ${horaMinuto(painel.gerado_em)}`
    : 'Só você e o André veem esta tela';

  const normais = painel ? painel.eventos.filter((e) => !e.teste) : [];
  const testes = painel ? painel.eventos.filter((e) => e.teste) : [];

  return (
    <div className="min-h-screen bg-primary-50">
      <Cabecalho
        claro
        voltar
        titulo="Painel da plataforma"
        subtitulo={subtitulo}
        larguraMaxima="max-w-[960px]"
        acao={
          aba === 'resumo' ? (
            <Botao
              variante="secundario"
              icone="ri-refresh-line"
              carregando={recarregando}
              onClick={() => void carregar(false)}
            >
              Atualizar
            </Botao>
          ) : undefined
        }
      />

      <main className="mx-auto w-full max-w-[960px] px-4 py-6 md:px-6 md:py-8">
        {(situacao === 'carregando-login') && <Carregando cartoes={4} />}

        {situacao === 'restrito' && (
          <EstadoVazio
            icone="ri-lock-line"
            titulo="Acesso restrito"
            acaoRotulo="Voltar ao início"
            aoClicarAcao={() => navigate('/')}
          />
        )}

        {situacao === 'erro' && (
          <Cartao className="p-6 text-center">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-danger-100 text-danger-600">
              <i className="ri-error-warning-line text-2xl" />
            </span>
            <p className="mt-4 text-base font-semibold text-foreground-950">
              Não foi possível carregar
            </p>
            <p className="mt-1 text-sm text-foreground-600">{erro}</p>
            <div className="mt-5 flex justify-center">
              <Botao variante="primario" icone="ri-refresh-line" onClick={() => void carregar(false)}>
                Tentar de novo
              </Botao>
            </div>
          </Cartao>
        )}

        {situacao === 'ok' && painel && (
          <>
            <div className="mb-5 inline-flex max-w-full overflow-x-auto rounded-full bg-background-100 p-1">
              {([
                { id: 'resumo', rotulo: 'Resumo' },
                { id: 'avisos', rotulo: 'Avisos no celular' },
                { id: 'leads', rotulo: 'Leads' },
                { id: 'senha', rotulo: 'Ajudar com senha' },
                { id: 'oracoes', rotulo: 'Orações' },
              ] as const).map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setAba(t.id)}
                  className={`aperta min-h-10 cursor-pointer whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                    aba === t.id
                      ? 'bg-background-50 text-primary-700 shadow-pcard'
                      : 'text-foreground-600 hover:text-foreground-900'
                  }`}
                >
                  {t.rotulo}
                </button>
              ))}
            </div>

            {aba === 'avisos' && <AvisosGerais />}

            {aba === 'leads' && <Leads />}

            {aba === 'senha' && <AjudarSenha />}

            {aba === 'oracoes' && <Oracoes />}

            {aba === 'resumo' && (
              <div className="space-y-6">
                <Tiles
                  totais={painel.totais}
                  pendencias={painel.pendencias}
                  mes={painel.mes}
                  aviso={painel.aviso}
                />

                <CartaoExclusoes aoAtualizar={() => void carregar(false)} />

                <section>
                  <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-foreground-500">
                    Eventos
                  </h2>
                  {normais.length > 0 ? (
                    <div className="space-y-4">
                      {normais.map((evento) => (
                        <CartaoEvento
                          key={evento.id}
                          evento={evento}
                          aoRegistrar={setRegistrandoAlvo}
                          aoAtualizar={() => void carregar(false)}
                        />
                      ))}
                    </div>
                  ) : (
                    <EstadoVazio
                      icone="ri-calendar-line"
                      titulo="Nenhum evento ainda"
                      descricao="Quando houver eventos com movimento, eles aparecem aqui."
                    />
                  )}
                </section>

                {testes.length > 0 && (
                  <section>
                    <button
                      type="button"
                      onClick={() => setTestesAberto((v) => !v)}
                      className="flex w-full cursor-pointer items-center justify-between gap-3 rounded-2xl border border-secondary-200 bg-white px-4 py-3 text-left transition-colors hover:bg-background-100"
                    >
                      <span className="min-w-0 text-sm font-semibold text-foreground-950">
                        Eventos de teste ({testes.length})
                      </span>
                      <span
                        className={`flex h-5 w-5 shrink-0 items-center justify-center text-foreground-500 transition-transform ${
                          testesAberto ? 'rotate-180' : ''
                        }`}
                      >
                        <i className="ri-arrow-down-s-line" />
                      </span>
                    </button>
                    {testesAberto && (
                      <div className="mt-3 space-y-4">
                        {testes.map((evento) => (
                          <CartaoEvento
                            key={evento.id}
                            evento={evento}
                            aoRegistrar={setRegistrandoAlvo}
                            aoAtualizar={() => void carregar(false)}
                          />
                        ))}
                      </div>
                    )}
                  </section>
                )}
              </div>
            )}
          </>
        )}
      </main>

      {registrandoAlvo && (
        <ModalRegistrarRepasse
          evento={registrandoAlvo}
          aoFechar={() => setRegistrandoAlvo(null)}
          aoFeito={(recado) => {
            setRegistrandoAlvo(null);
            mostrarToast(recado, 4000, 'sucesso');
            void carregar(false);
          }}
        />
      )}
    </div>
  );
}